// Copyright 2017 Sorama B.V.

import { autoinject, BindingEngine, computedFrom, Disposable } from "aurelia-framework";
import { WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import { MeasurementWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/MeasurementModuleCatalog";
import { RecordingMetadata } from "datatypes/Containers";
import { ScanArea } from "datatypes/ScanStep";
import { MeasurementHealthIssueType, Point2D, Point3D } from "datatypes/SoramaLocalApi";
import { URA } from "libraries/Math/URA";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import {
    LocalStoredDefaultValue,
    WorkflowPropertyBindingMode,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { IWorkflowModuleController } from "libraries/WorkflowFramework/Interfaces/IWorkflowRouter";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import {
    ChannelSelectionModalReturnState,
    ChannelSelectionModalState,
} from "modules/modals/ChannelSelection/ChannelSelectionModal";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { ChannelSelection } from "services/ChannelSelection/ChannelSelection";

import { ManagementHelperFunctions } from "services/Hardware/Management/ManagementHelperFunctions";
import { IMeasurementAcquisitionService } from "services/Measurement/IMeasurementAcquisitionService";

import { IRecordingGroupContainerStorageService } from "services/Storage/IRecordingGroupContainerStorageService";
import { IStorageService } from "services/Storage/IStorageService";

import { MotionDeviceService } from "services/MotionDevice/MotionDeviceService";
import { NotificationUrgency } from "services/Notification/INotificationService";

/**
 * MODULE OPERATION DESCRIPTION - MeasurementPreparationModule
 * The Measurement Preparation Module provides functionality for preparing/setting up a measurement
 * by having the user insert all measurement parameters.
 * Intended for use by customers already familiar with NAH/Beamforming who also know which settings have which effect.
 */
@autoinject
export class MeasurementPreparationModule implements IWorkflowModuleLifetime {
    //#region Constants
    public ignoreIssues: MeasurementHealthIssueType[] = [MeasurementHealthIssueType.InsufficientThroughput];

    private static NearFieldAnalysis = "Near Field Analysis";
    private static FarFieldAnalysis = "Far Field Analysis";
    private static TimeSpectralAnalysis = "Time & Spectral Analysis";
    //#endregion

    //#region View Properties
    private subscriptions: Disposable[];

    private measurementDeviceDimensions: Point2D;
    private moduleInstance: IWorkflowModuleInstance;

    @computedFrom("scanningArea", "measurementDeviceDimensions")
    private get widthInArrays(): number {
        if (!this.measurementDeviceDimensions || !this.scanningArea) return 1;

        return Math.max(1, Math.round(this.scanningArea.width / this.measurementDeviceDimensions.x));
    }

    private set widthInArrays(value: number) {
        if (this.measurementDeviceDimensions) {
            this.scanningArea = {
                height: this.scanningArea.height,
                width: this.measurementDeviceDimensions.x * value,
                offsetX: this.scanningArea.offsetX,
                offsetY: this.scanningArea.offsetY,
                offsetZ: this.scanningArea.offsetZ,
            };
        }
    }

    @computedFrom("scanningArea", "measurementDeviceDimensions")
    private get heightInArrays(): number {
        if (
            !this.measurementAcquisitionService ||
            !this.measurementAcquisitionService.activeSetup ||
            !this.scanningArea
        )
            return 1;

        if (!this.measurementDeviceDimensions || !this.scanningArea) return 1;

        return Math.max(1, Math.round(this.scanningArea.height / this.measurementDeviceDimensions.y));
    }

    private set heightInArrays(value: number) {
        if (this.measurementDeviceDimensions) {
            this.scanningArea = {
                height: this.measurementDeviceDimensions.y * value,
                width: this.scanningArea.width,
                offsetX: this.scanningArea.offsetX,
                offsetY: this.scanningArea.offsetY,
                offsetZ: this.scanningArea.offsetZ,
            };
        }
    }

    @computedFrom("measurementAcquisitionService.isReserved.available")
    public get isDeviceAvailable(): boolean {
        return this.measurementAcquisitionService ? this.measurementAcquisitionService.isReserved.available : false;
    }

    @computedFrom("measurementAcquisitionService.activeSetup", "channelSelection")
    public get maxMeasurementDuration(): number {
        return ManagementHelperFunctions.getMaximumMeasurementDuration(this.measurementAcquisitionService.activeSetup);
    }

    @computedFrom("recommendedAnalysis")
    private get recommendedAnalysisIcon(): string {
        const analysisName = this.recommendedAnalysis;

        if (analysisName === MeasurementPreparationModule.FarFieldAnalysis) return "fas fa-rss";
        else if (analysisName === MeasurementPreparationModule.NearFieldAnalysis) return "fas fa-chess-board";
        else if (analysisName === MeasurementPreparationModule.TimeSpectralAnalysis) return "fas fa-hourglass-half";

        return "";
    }

    private get recommendedAnalysis(): string {
        if (!this.measurementDeviceDimensions) return "-";

        if (
            this.measurementDistance < Math.min(this.measurementDeviceDimensions.x, this.measurementDeviceDimensions.y)
        ) {
            return MeasurementPreparationModule.NearFieldAnalysis;
        } else if (
            this.scanningArea.height == this.measurementDeviceDimensions.y &&
            this.scanningArea.width == this.measurementDeviceDimensions.x
        ) {
            return MeasurementPreparationModule.FarFieldAnalysis;
        } else {
            // far-field scanning measurement
            return MeasurementPreparationModule.TimeSpectralAnalysis;
        }
    }

    public measurementNotActive = false;

    private isVideoPreviewActive: boolean;
    private videoPreviewModule: IWorkflowModuleController;

    //#endregion

    //#region Workflow Properties
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private recordingGroupContainerId: string;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private selectedMeasurementArea: "single" | "quick" | "custom" = "single";

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public measurementSetupId: string;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.oneWay })
    public isMeasurementComplete: boolean;

    // Sound camera to object distance in [m]
    @workflowProperty({
        bindingMode: WorkflowPropertyBindingMode.twoWay,
        defaultValue: { defaultValue: 0.03, key: "measurementDistance" } as LocalStoredDefaultValue,
    })
    public measurementDistance: number;

    // Measurement duration in [s]
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public measurementDuration = 1;

    // Scan Plan Settings
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public scanningArea: ScanArea;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public channelSelection: ChannelSelection;
    //#endregion

    //#region Constructor & Aurelia Callbacks
    constructor(
        private readonly measurementAcquisitionService: IMeasurementAcquisitionService,
        private readonly storageService: IStorageService,
        private readonly recordingGroupContainerStorageService: IRecordingGroupContainerStorageService,
        private readonly bindingEngine: BindingEngine,
        protected readonly shellViewManager: ShellViewManager,
        protected readonly motionService: MotionDeviceService
    ) {}

    public activateModule(moduleInstance: IWorkflowModuleInstance): Promise<void> | void {
        this.moduleInstance = moduleInstance;

        this.subscriptions = [
            this.bindingEngine
                .propertyObserver(this.measurementAcquisitionService, "_activeSetup")
                .subscribe(this.onSelectedSetupPropertiesChanged),
            this.bindingEngine
                .propertyObserver(this.measurementAcquisitionService, "isReserved")
                .subscribe(this.onSelectedSetupPropertiesChanged),
            this.bindingEngine
                .propertyObserver(this.measurementAcquisitionService, "isAcquiring")
                .subscribe(this.updateIsMeasurementStarted),
            this.bindingEngine
                .propertyObserver(this.measurementAcquisitionService, "_channelSelection")
                .subscribe(this.onSelectedSetupPropertiesChanged),
        ];

        this.moduleInstance.getWorkflow().subscribeModulesChanged(this.videoModulesChanged);

        if (this.moduleInstance.getWorkflow().moduleTypeExists(MeasurementWorkflowModuleCatalog.VideoModule)) {
            this.videoPreviewModule = this.moduleInstance
                .getWorkflow()
                .getModules(MeasurementWorkflowModuleCatalog.VideoModule)[0];
            this.isVideoPreviewActive = true;
        }

        // Check if we have a recordingGroup container id, if so, check if it exists, if not, create it now
        const existsPromise = this.recordingGroupContainerId
            ? this.storageService
                  .load(this.recordingGroupContainerId)
                  .then(() => true)
                  .catch(() => false)
            : Promise.resolve(false);

        // Do not wait with the activation of the module, just continue
        existsPromise
            .then((exists) => {
                if (!exists) {
                    // Container does not exists, create a new container
                    return this.recordingGroupContainerStorageService.create().then((x) => {
                        this.recordingGroupContainerId = x.id;
                        this.measurementNotActive = true;
                    });
                }
            })
            .then(() => {
                // We should have a container ready now, load metadata
                return this.loadStoredMetadata().then(() => {
                    if (this.measurementAcquisitionService.activeSetup) {
                        return this.onSelectedSetupPropertiesChanged();
                    }
                });
            })
            .catch((e) => {
                this.moduleInstance.createNotification().withUrgency(NotificationUrgency.Error).withMessage(e.message);
            });
    }

    public deactivateModule(): Promise<void> | void {
        while (this.subscriptions.length) this.subscriptions.pop().dispose();

        // Check if we have a recordingGroupContainerId
        if (this.recordingGroupContainerId) {
            return this.deleteIfEmpty(this.recordingGroupContainerId);
        }
    }

    private onSelectedSetupPropertiesChanged = () => {
        // Check if the duration time is still valid
        this.measurementDuration = Math.min(this.measurementDuration, this.maxMeasurementDuration);

        if (this.measurementAcquisitionService.activeSetup) {
            this.measurementSetupId = this.measurementAcquisitionService.activeSetup.id;

            // Assign channel selection, if not available yet
            if (!this.measurementAcquisitionService.activeSetup.channelSelection) {
                // If no selection given, select all mics
                const channelSelection = new ChannelSelection(
                    this.measurementAcquisitionService.activeSetup.sensorPositions.map(() => true)
                );
                this.updateChannelSelection(channelSelection);
            }

            // Get dimensions of the physical array
            const uraProperties = URA.getUraProperties3D(
                this.measurementAcquisitionService.activeSetup.sensorPositions.map(
                    (x) => <Point3D>{ x: x.X, y: x.Y, z: x.Z }
                )
            );

            if (uraProperties.isUra) {
                this.measurementDeviceDimensions = { x: uraProperties.width, y: uraProperties.height };
            } else {
                // Just use min and max of arrays, without taking the edges into account. This is not correct for sunflower array.
                let pos = this.measurementAcquisitionService.activeSetup.sensorPositions.map((x) => x.Y);
                const height = Math.max(...pos) - Math.min(...pos);
                pos = this.measurementAcquisitionService.activeSetup.sensorPositions.map((x) => x.X);
                const width = Math.max(...pos) - Math.min(...pos);
                this.measurementDeviceDimensions = { x: width, y: height };
            }

            // Set the width/height of scanningArea
            this.setScanningArea(this.measurementDeviceDimensions);
        } else {
            this.measurementSetupId = undefined;
            this.measurementDeviceDimensions = undefined;
        }

        // If the scanning area is not set, set the area
        if (!this.scanningArea && this.measurementDeviceDimensions) {
            this.scanningArea = {
                width: this.measurementDeviceDimensions.x,
                height: this.measurementDeviceDimensions.y,
                offsetX: 0,
                offsetY: 0,
                offsetZ: 0,
            };
        }

        this.updateStoredMetadata();
    };

    public recordingGroupContainerIdChanged(newId: string, oldId: string): Promise<void> {
        let promise = Promise.resolve();

        // If the old id is not null, check if there are any recordings in it, if not, delete it
        if (oldId && oldId !== "" && newId !== oldId) {
            promise = this.deleteIfEmpty(oldId);
        }

        return promise.then(() => {
            return this.loadStoredMetadata().then(() => {
                return this.updateStoredMetadata();
            });
        });
    }

    public setScanningArea(measurementDeviceDimensions: Point2D): void {
        if (this.scanningArea && this.scanningArea.width == 0) {
            this.scanningArea.width = measurementDeviceDimensions.x;
        }

        if (this.scanningArea && this.scanningArea.height == 0) {
            this.scanningArea.height = measurementDeviceDimensions.y;
        }
    }

    //#endregion

    //#region User Input
    public acceptSettings(): void {
        let recordingModules = this.moduleInstance
            .getWorkflow()
            .getModules(MeasurementWorkflowModuleCatalog.RecordingMetadataModule);
        let measureModules = this.moduleInstance
            .getWorkflow()
            .getModules(MeasurementWorkflowModuleCatalog.MeasureModule);

        const promises: Promise<unknown>[] = [];

        if (recordingModules.length == 0) {
            promises.push(
                this.moduleInstance
                    .getWorkflow()
                    .addModule(MeasurementWorkflowModuleCatalog.RecordingMetadataModule)
                    .then((x) => {
                        recordingModules = [x];
                    })
            );
        }

        if (measureModules.length == 0) {
            promises.push(
                this.moduleInstance
                    .getWorkflow()
                    .addModule(MeasurementWorkflowModuleCatalog.MeasureModule)
                    .then((x) => {
                        measureModules = [x];
                    })
            );
        }

        Promise.all(promises).then(() => {
            measureModules[0].makeActive();
        });
    }

    private showChannelSelectionModal() {
        if (
            !this.measurementAcquisitionService.activeSetup ||
            this.measurementAcquisitionService.activeSetup.length !== 1
        )
            return;

        this.moduleInstance
            .createModal<ChannelSelectionModalState, ChannelSelectionModalReturnState>({
                ...WorkflowModalCatalog.ChannelSelectionModal,
                smallModalSize: this.measurementAcquisitionService.activeSetup.sensorPositions.length <= 64,
            })
            .withState(<ChannelSelectionModalState>{
                frontEnd: this.measurementAcquisitionService.activeSetup[0].measurementDevice.frontEnd,
            })
            .whenClosed((x) => {
                this.updateChannelSelection(x.selectedSelection);
            })
            .show();
    }

    private updateChannelSelection(channelSelection: ChannelSelection): void {
        if (
            this.measurementAcquisitionService.activeSetup &&
            this.measurementAcquisitionService.activeSetup.length === 1
        ) {
            const channelSelections: { [id: string]: ChannelSelection } = {};
            channelSelections[this.measurementAcquisitionService.activeSetup[0].id] = channelSelection;

            this.measurementAcquisitionService.setChannelSelections(channelSelections).then(() => {
                this.channelSelection = channelSelection;
                this.onSelectedSetupPropertiesChanged();
            });
        }
    }

    //#endregion

    //#region Helper Methods
    private showCustomMeasurementArea(): void {
        if (!this.moduleInstance) return;

        const areaModules = this.moduleInstance
            .getWorkflow()
            .getModules(MeasurementWorkflowModuleCatalog.MeasurementAreaModule);
        if (areaModules.length > 0) {
            areaModules[0].makeActive();
        } else {
            this.moduleInstance.getWorkflow().addModule(MeasurementWorkflowModuleCatalog.MeasurementAreaModule);
        }
    }

    private measurementDistanceChanged(): void {
        this.updateStoredMetadata();
    }

    private updateStoredMetadata(): Promise<void> {
        if (!this.moduleInstance) return;

        return this.moduleInstance.dispatch(() => {
            return this.recordingGroupContainerStorageService
                .updateKnownMetadata(this.recordingGroupContainerId, "distance", this.measurementDistance)
                .catch((e: Error) => {
                    this.moduleInstance
                        .createNotification()
                        .withUrgency(NotificationUrgency.Error)
                        .withMessage(e.message)
                        .show();
                });
        });
    }

    private loadStoredMetadata(): Promise<void> {
        if (!this.moduleInstance) return Promise.resolve();

        return this.moduleInstance.dispatch(() => {
            if (this.recordingGroupContainerId) {
                return this.recordingGroupContainerStorageService.load(this.recordingGroupContainerId).then((x) => {
                    if (x.metadata.distance) {
                        this.measurementDistance = x.metadata.distance;
                    }
                    if (x.metadata.steps) {
                        // Restore channel selection, based on a finished step
                        const finishedStep = x.metadata.steps.find((x) => !!x.recordingId);
                        if (finishedStep) {
                            return this.storageService
                                .getMetadata<RecordingMetadata>(finishedStep.recordingId)
                                .then((recordingMetadata) => {
                                    if (recordingMetadata.files.sound) {
                                        const existingChannelSelection = <boolean[]>(
                                            recordingMetadata.files.sound.channelSelection
                                        );
                                        this.updateChannelSelection(new ChannelSelection(existingChannelSelection));
                                    }
                                })
                                .catch(() => {
                                    // Somebody screwed up the recording. So keep the original channel selection.
                                });
                        }
                    }

                    this.measurementNotActive = !x.metadata.steps.some((y) => !!y.recordingId);
                });
            }
        });
    }

    private updateIsMeasurementStarted = (): void => {
        const promise = this.recordingGroupContainerId
            ? this.recordingGroupContainerStorageService
                  .load(this.recordingGroupContainerId)
                  .then((x) => x.metadata.steps.some((y) => !!y.recordingId))
            : Promise.resolve(false);

        promise.then((x) => {
            this.measurementNotActive = !x;
        });
    };

    private deleteIfEmpty(id: string): Promise<void> {
        return this.recordingGroupContainerStorageService
            .load(id)
            .then((x) => {
                // We have no measurement at all, so remove it for now. Maybe in the future we can add preparing a measurement.
                if (
                    !x.metadata ||
                    !x.metadata.steps ||
                    x.metadata.steps.length === 0 ||
                    x.metadata.steps.every((y) => !y.recordingId)
                ) {
                    return this.recordingGroupContainerStorageService.delete(id);
                }
            })
            .catch(() => {
                // Incorrect format most likely, make sure it is deleted
                return this.storageService.delete(id);
            })
            .catch(() => {});
    }

    private toggleVideoPreview(): void {
        if (this.isVideoPreviewActive) {
            this.videoPreviewModule.close();
        } else {
            this.moduleInstance
                .getWorkflow()
                .addModule(MeasurementWorkflowModuleCatalog.VideoModule)
                .then((x) => {
                    this.videoPreviewModule = x;
                });
        }
    }

    private videoModulesChanged = () => {
        this.isVideoPreviewActive = this.moduleInstance
            .getWorkflow()
            .moduleTypeExists(MeasurementWorkflowModuleCatalog.VideoModule);
    };

    protected selectedMeasurementAreaChanged(): void {
        if (this.selectedMeasurementArea == "single") {
            // change back to 1x1 measurement
            this.heightInArrays = 1;
            this.widthInArrays = 1;
        }
    }
    //#endregion
}
